/* eslint-disable arrow-body-style */
import { getHighlightDefaultMetrics, getHighlightList } from '@home/services/studio';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl, useParams } from 'umi';
import { studioAccountsPageOfficialAccounts } from '@op/components/FilterCommon/utils/monitorUtil';
import { canRequest } from '../../../utils';
import { calculateBrandHealthRate } from '@op/utils/text';
import { dynamicString } from '@home/utils/dynamicString';
import { StudioContext } from '@home/pages/studio/context';

interface IProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  config: any;
  gameInfo?: { [key: string]: string; };
  filter: { [key: string]: string; };
  defaultList: IHighlightItem[];
  changeParams: Array<string>;
  filterCond: any;
  highlightCnMap: Record<string, string>;
}

interface IHighlightItem {
  key: string;
  titleEn: string;
  titleCn: string;
  executive_summary: string;
  
}

interface Item {
  aliases: string;
  value: number;
}

export default function HighlightsWrap(props: IProps) {
  const { className, style, children, config, filter, gameInfo, defaultList, changeParams, highlightCnMap, filterCond, ...restProps } = props || {};
  const { monitor_by_account_category } = filterCond || {};
  const { monitory } = monitor_by_account_category || {};
  const { editPermission } = useContext(StudioContext);
  const accounts = studioAccountsPageOfficialAccounts(monitory);
  const accountList: string[] = [];
  Object.keys(accounts)?.forEach(key => {
    accounts[key]?.forEach((acc: any) => {
      accountList.push(acc.account);
    });
  });

  const { date_type: dateType, start_time: startTime, end_time: endTime, } = filter || {};
  const { studioId } = useParams() || ({} as any);
  const [highlightDefaultData, setHighlightDefaultData] = useState<IHighlightItem[]>([]);
  const [highlightData, setHighlightData] = useState<IHighlightItem[]>([]);
  const [defaultError, setDefaultError] = useState(false);
  const [error, setError] = useState(false);
  const [defaultLoading, setDefaultLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isEdited, setIsEdited] = useState(false);
  const [clickAddHighlight, setClickAddHighlight] = useState(false);
  const { formatMessage } = useIntl();

  // 将接口返回的数据映射为高亮数据
  const defaultDataFormat = (data: Record<string, Item>, changeParams: string[]) => {
    // 千分位格式化函数
    const formatLargeNumber = (num: number) => {
      return typeof num === 'number' ? num.toLocaleString() : num;
    };
  
    return Object.values(data)?.map((item: Item) => {
      const { aliases, value } = item;
  
      // 判断是否在 changeParams 中
      const shouldIncludeChange = changeParams.includes(aliases);
  
      // 格式化小数保留两位，整数保持原样，并应用千分位格式
      let formattedValue;

      if (typeof value === "string") {
        formattedValue = value;
      } else if (typeof value === "number" && value % 1 !== 0) {
        formattedValue = formatLargeNumber(Math.abs(Number(value.toFixed(2))));
      } else {
        formattedValue = formatLargeNumber(Math.abs(Number(value)));
      }
  
      // 如果 aliases 是 brand_health，计算 brandHealth 并加入 health 属性
      if (aliases === "brand_health") {
        const brandHealth = calculateBrandHealthRate(value);
        return {
          [aliases]: formattedValue,
          [`${aliases}.change`]: value > 0 ? "increase" : "decrease",
          health: formatMessage({ id: brandHealth.text }),
        };
      }
  
      // 默认返回其他项
      return shouldIncludeChange
        ? {
          [aliases]: formattedValue,
          [`${aliases}.change`]: value > 0 ? "increase" : "decrease",
        }
        : {
          [aliases]: formattedValue,
        };
    });
  };
  

  // 获取高亮报告的默认数据
  const getDefaultListData = async () => {
    const params = {
      social_media_by_account: filterCond?.social_media_by_account || undefined,
      social_media_by_keyword: undefined,
      edition_unified_id: filter?.edition_unified_id,
      entity_type: filter?.entity_type,
      id_type: filter?.id_type,
      date_type: dateType,
      start_time: startTime,
      end_time: endTime,
      accounts: accountList
    };

    try {
      // 获取高亮列表
      const res = await getHighlightDefaultMetrics(params);
      const mappedData = defaultDataFormat(res, changeParams);
      const resultList = defaultList.map(item => {
        return {
          ...item,
          executive_summary: dynamicString(item?.executive_summary, Object.assign({}, ...mappedData))
        };
      });
      setHighlightDefaultData(resultList);

      setDefaultError(false);
      setDefaultLoading(false);
    } catch (error) {
      setDefaultError(true);
      setDefaultLoading(false);
    }
  };

  // 获取高亮报告数据
  const fetchData = async () => {
    const params = {
      studio_code: studioId,
      unified_id: gameInfo?.edition_unified_id,
      period: dateType,
      start_date: moment(startTime).format('YYYY-MM-DD'),
      end_date: moment(endTime).format('YYYY-MM-DD'),
    };

    try {
      // 获取高亮列表
      const { highlights, is_edited } = await getHighlightList(params);
      const mappedData = highlights.map((item: any) => ({
        highlight: item.highlight,
        defaultHighlight: item.highlight,
        titleEn: item.highlight,
        titleCn: highlightCnMap[item.highlight],
        executive_summary: item.executive_summary,
        id: item.id || 0,
      }));

      setHighlightData(mappedData.length > 0 ? mappedData : null);
      setIsEdited(is_edited);
      setError(false);
      setLoading(false);
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  };

  // 当 dateType 变化时，重新获取数据
  useEffect(() => {
    if (!dateType) return;
    fetchData();
    setLoading(true);
  }, [filter, studioId, gameInfo, dateType]);

  // 当 filter 和 monitory 变化时，重新获取默认数据
  useEffect(() => {
    if (canRequest(filter) && monitory) {
      getDefaultListData();
      setDefaultLoading(true);
    }
  }, [filter, monitory]);

  // 生成 reportData 并把 index 加到 item
  const reportData = useMemo(() => {
    if(isEdited && !highlightData){
      return [];
    }
    return (highlightData || highlightDefaultData)?.map((item: any, index: number) => ({
      index,
      ...item,
    }));
  }, [highlightData, highlightDefaultData,isEdited]);

  return (
    <>
      {React.Children.map(children, (child: any) => React.cloneElement(
        child,
        {
          ...restProps,
          editPermission,
          isEdited,
          reportData,
          filter,
          highlightCnMap,
          loading: loading || defaultLoading,
          error: error || defaultError,
          fetchData,
          gameInfo,
          clickAddHighlight,
          setClickAddHighlight,
          state: {}
        }
      ))}
    </>
  );
}